
import React from "react";
import { 
  Briefcase, 
  Users, 
  Clock, 
  DollarSign, 
  MapPin, 
  CheckCircle 
} from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface CollaborationCardProps {
  project: any;
  onViewDetails: () => void;
}

export const CollaborationCard = ({ project, onViewDetails }: CollaborationCardProps) => {
  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow border border-gray-200">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <CardTitle className="text-lg">{project.title}</CardTitle>
          <Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-200">
            {project.category}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pb-3 space-y-3">
        <p className="text-sm text-gray-700 line-clamp-2">{project.description}</p>
        
        <div className="flex flex-wrap gap-1 mb-1">
          {project.skillsNeeded.slice(0, 3).map((skill: string, i: number) => (
            <Badge key={i} variant="outline" className="bg-gray-50">
              {skill}
            </Badge>
          ))}
          {project.skillsNeeded.length > 3 && (
            <Badge variant="outline">+{project.skillsNeeded.length - 3}</Badge>
          )}
        </div>
        
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4 text-gray-500" />
            <span>{project.timeline}</span>
          </div>
          <div className="flex items-center gap-1">
            <DollarSign className="h-4 w-4 text-gray-500" />
            <span>{project.budget}</span>
          </div>
          <div className="flex items-center gap-1">
            <Users className="h-4 w-4 text-gray-500" />
            <span>{project.teamSize} members</span>
          </div>
          <div className="flex items-center gap-1">
            <MapPin className="h-4 w-4 text-gray-500" />
            <span>{project.location}</span>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between items-center border-t pt-3 bg-gray-50">
        <div className="flex items-center gap-2">
          <Avatar className="h-6 w-6">
            <AvatarImage src={project.owner.avatar} />
            <AvatarFallback>{project.owner.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <span className="text-sm">{project.owner.name}</span>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onViewDetails}
          className="text-indigo-600 border-indigo-200 hover:border-indigo-300 hover:bg-indigo-50"
        >
          View Details
        </Button>
      </CardFooter>
    </Card>
  );
};
